import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SummaryButtonGroup } from 'modules/ApprovedLoanInput/styles';
import { INITIAL_VALUES } from 'modules/ApprovedLoanInput/approvedLoanInput.const';
import { Button } from 'modules/core/components';
import { getIncome, getIncomeCalculatorReportData } from 'store/selectors/approvedLoanInput.selector';
import { getSubventionYearDataThunk } from 'actions/controlActions';
import { AppDispatch } from 'store/store';
import { ButtonVariant } from 'modules/core/enums';
import { useIncomeReportButton, useModalState, useVehicleFormReset } from 'modules/core/hooks';
import { MODAL_DIALOG_ID, STATIC_IMAGE_URLS } from 'modules/core/constants';
import {
  setIncome,
  resetIncome,
  resetVehicles,
  resetByStockOptions,
  incrementResetCount,
} from 'store/reducers/approvedLoan.reducer';
import { IncomeCalculator } from 'components/IncomeCalculator';
import { IIncomeForm } from 'components/IncomeCalculator/types';
import { ButtonIcon } from 'modules/ProbabilityEstimator/ConsumerInformation/styles';

export const HeaderComponent: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const incomeCalculatorData = useSelector(getIncomeCalculatorReportData);
  const { isButtonPrintDisabled, onButtonPrintClick } = useIncomeReportButton(incomeCalculatorData);

  const {
    isModalDialogActive: isIncomeDialogActive,
    handleOpenDialog: handleIncomeDialogOpen,
    handleCloseDialog: handleIncomeDialogClose,
  } = useModalState([MODAL_DIALOG_ID.INCOME_CALCULATOR]);

  useEffect(() => {
    dispatch(getSubventionYearDataThunk());
  }, [dispatch]);

  const onIncomeCalculatorSubmit = useCallback(
    (incomeForm: IIncomeForm) => {
      dispatch(setIncome(incomeForm));
    },
    [dispatch],
  );

  const resetStateCallback = useCallback(() => {
    dispatch(resetVehicles());
    dispatch(resetIncome());
    dispatch(incrementResetCount());
  }, [dispatch]);

  const { resetAll } = useVehicleFormReset(INITIAL_VALUES, resetStateCallback, () => dispatch(resetByStockOptions()));

  return (
    <SummaryButtonGroup>
      <Button
        title={t('components.approvedLoanInput.approvedLoanTerms.buttons.incomeCalculator')}
        onClick={handleIncomeDialogOpen}
      />
      <IncomeCalculator
        show={isIncomeDialogActive}
        onHide={handleIncomeDialogClose()}
        onIncomeCalculatorSubmit={onIncomeCalculatorSubmit}
        incomesSelector={getIncome}
      />
      <ButtonIcon
        iconUrl={STATIC_IMAGE_URLS.icons.print}
        data-testid="print-income"
        onClick={onButtonPrintClick}
        disabled={isButtonPrintDisabled}
      />
      <Button
        title={t('components.approvedLoanInput.approvedLoanTerms.buttons.reset')}
        onClick={resetAll}
        buttonTheme={ButtonVariant.OUTLINE}
      />
    </SummaryButtonGroup>
  );
};
